import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchAlphaTutorialState,
  patchTutorialActive,
  patchTutorialExpired,
  patchTutorialStep,
  TutorialStateResponse,
  TutorialStatus,
} from '../../shared/api/api';
import moment from 'moment';
import analytics from '../../shared/services/analytics/analytics';

export interface BaseTutorialStore {
  isTutorialExpired: boolean;
  isTutorialModeActive: boolean;
  currentlyViewedStep: number | null;
  maxUnlockedTutorialStep: number | null;
  tutorialStartDate?: string | null;
  tutorialTotalSteps: number;
  status: TutorialStatus;
  slug: string | null;
}

export interface BaseTutorialStoreProps {
  tutorial: BaseTutorialStore;
}

const initialState: BaseTutorialStore = {
  isTutorialExpired: false,
  isTutorialModeActive: false,
  currentlyViewedStep: 0,
  maxUnlockedTutorialStep: 0,
  tutorialStartDate: '2025-02-01',
  tutorialTotalSteps: 5,
  status: 'LOADING',
  slug: null,
};

//start State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: false,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 0,
//   maxUnlockedTutorialStep: 0,
//   tutorialStartDate: null,
//   tutorialTotalSteps: 5,
// };

//expired State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: true,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 2,
//   maxUnlockedTutorialStep: 4,
//   tutorialStartDate: '2025-01-01',
//   tutorialTotalSteps: 5,
// };

// final State:
// const initialState: BaseTutorialStore = {
//   isTutorialExpired: false,
//   isTutorialModeActive: false,
//   currentlyViewedStep: 4,
//   maxUnlockedTutorialStep: 4,
//   tutorialStartDate: '2025-02-01',
//   tutorialTotalSteps: 5,
// }

function getCurrentStepNumber(payload: TutorialStateResponse) {
  if (payload.currentStep == null) {
    return 0;
  }
  return typeof payload.currentStep === 'number' ? payload.currentStep : Number.parseInt(payload.currentStep, 10);
}

export const TutorialSlice = createSlice({
  name: 'Tutorial',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    advanceTutorial(state, action: PayloadAction<number>) {
      const stepIndex = action.payload;
      //if the new maxUnlockedTutorialStep is not loaded yet, advance to the next step
      const newStep = Math.max(state.maxUnlockedTutorialStep ?? 0, stepIndex + 1);
      state.currentlyViewedStep = newStep;
      patchTutorialStep(newStep);
    },
    setNextStep(state) {
      const currentlyViewedStep = state.currentlyViewedStep ?? 0;
      analytics.trackCustomEvent('tutorial_click_next', { event_label: currentlyViewedStep.toString() });
      const isFinalStep =
        currentlyViewedStep === state.tutorialTotalSteps - 1 && state.maxUnlockedTutorialStep === state.tutorialTotalSteps - 1;
      if (!isFinalStep && currentlyViewedStep >= (state.maxUnlockedTutorialStep ?? 0)) {
        return;
      }
      const newStep = currentlyViewedStep + 1;
      state.currentlyViewedStep = newStep;
      patchTutorialStep(newStep);
    },
    setPreviousStep(state) {
      const currentlyViewedStep = state.currentlyViewedStep ?? 0;
      analytics.trackCustomEvent('tutorial_click_previous', { event_label: currentlyViewedStep.toString() });
      if (currentlyViewedStep <= 0) {
        return;
      }
      const newStep = currentlyViewedStep - 1;
      state.currentlyViewedStep = newStep;
      patchTutorialStep(newStep);
    },
    setState(state, action: PayloadAction<TutorialStateResponse>) {
      const payload = action.payload;
      const currentStepNumber = getCurrentStepNumber(payload);
      return {
        isTutorialExpired: payload.expired,
        isTutorialModeActive: payload.active,
        currentlyViewedStep: currentStepNumber,
        maxUnlockedTutorialStep: payload.maxUnlockedStep - 1, //front-end uses 0-based index, back-end uses 1-based index
        tutorialStartDate: payload.startDatetime,
        tutorialTotalSteps: payload.totalSteps,
        status: payload.status,
        slug: payload.notificationSlug,
      };
    },
    enableTutorialMode(state) {
      //if the tutorial was not started, another action is needed (startTutorial)
      if (state.tutorialStartDate != null) {
        state.isTutorialModeActive = true;
        patchTutorialActive(true);
      }
    },
    disableTutorialMode(state) {
      state.isTutorialModeActive = false;
      patchTutorialActive(false);
    },
    startTutorial(state) {
      analytics.trackCustomEvent('tutorial_click_start');
      state.tutorialStartDate = moment().format('YYYY-MM-DD');
      state.isTutorialModeActive = true;
      state.currentlyViewedStep = 0;
      state.maxUnlockedTutorialStep = 0;
      patchTutorialActive(true);
    },
    resetTutorialState(state) {
      state.tutorialStartDate = moment().format('YYYY-MM-DD');
      state.isTutorialExpired = false;
      state.currentlyViewedStep = 0;
      state.maxUnlockedTutorialStep = 0;
    },
  },
});

//write async action that would restart tutorial as above, then set tutorial state based on the response

export const TutorialAsyncActions = {
  restartTutorial: () => async (dispatch: any) => {
    analytics.trackCustomEvent('tutorial_click_restart');
    dispatch(TutorialActions.resetTutorialState());
    const response = await patchTutorialExpired();
    dispatch(TutorialActions.setState(response));
  },
};
export const TutorialActions = TutorialSlice.actions;
export const setInitialTutorialState = () => async (dispatch: any) => {
  fetchAlphaTutorialState().then(tutorialState => {
    if (tutorialState) {
      dispatch(TutorialActions.setState(tutorialState));
    }
  });
};

export const tutorialReducer = TutorialSlice.reducer;
